import { debounce } from 'Assets/js/lib/helpers'

export default function Carousel(
  node,
  {
    cell = '.carousel--cell',
    viewport = '.carousel--viewport',
    dot = '.carousel--dot',
    initial = 0,
    scrollBehaviour = 'smooth',
    stepSize = null,
    autoPlay = null, // numeric ex: 3000
    interactionDelay = 10,
  } = {
    cell: '.carousel--cell',
    viewport: '.carousel--viewport',
    dot: '.carousel--dot',
    initial: 0,
    scrollBehaviour: 'smooth',
    stepSize: null,
    autoPlay: null, // numeric ex: 3000
    interactionDelay: 10,
  },
) {
  this.loop = !!autoPlay
  this.node = node ?? null
  this.viewport =
    node.querySelector(viewport) ??
    node.querySelector('[class*="viewport"]') ??
    null

  if (!this.viewport || !this.node) {
    throw new Error('No carousel container or viewport detected')
  }

  this.next = node.querySelector('[data-direction="next"]') ?? null
  this.previous = node.querySelector('[data-direction="previous"]') ?? null
  this.cells = Array.from(this.viewport.querySelectorAll(cell))
  this.dots = Array.from(node.querySelectorAll(dot)) ?? null
  this.start = 0
  this.end = this.cells.length - 1
  this.initHasHappened = false
  this.currentStart = null
  this.currentEnd = null
  this.stepSize = 1
  this.initialWidth = this.viewport.getBoundingClientRect().width
  this.initialHeight = this.viewport.getBoundingClientRect().height

  let scrollOffsetLeft = parseFloat(getComputedStyle(this.viewport).paddingLeft)
  let autoPlayTimer = null

  if (initial !== 0 && !this.initHasHappened) {
    this.viewport.scrollLeft = this.cells[initial].offsetLeft - scrollOffsetLeft
  }

  const getCurrentStart = () => {
    const startIndex = this.cells.findIndex((ele) =>
      ele.classList.contains('current-cell'),
    )
    return startIndex < 0 ? this.start : startIndex
  }

  const getCurrentEnd = () => {
    const endingIndex = [...this.cells]
      .reverse()
      .findIndex((ele) => ele.classList.contains('current-cell'))

    return endingIndex >= 0 ? this.end - endingIndex : endingIndex
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle(
          'current-cell',
          entry.intersectionRatio > 0.925,
        )
      })

      if (!this.initHasHappened) setup()
    },
    {
      root: this.viewport,
      threshold: [0, 0.5, 0.8, 0.85, 0.9, 0.95, 1],
    },
  )

  this.cells.forEach((cell) => observer.observe(cell))

  if (this.dots.length > 0) {
    this.dots.forEach((dot) => {
      const dotCellRef = Number(dot.getAttribute('data-cell'))

      this.node.addEventListener('settle', ({ detail }) => {
        dot.classList.toggle(
          'active',
          dotCellRef >= detail.currentStart && dotCellRef <= detail.currentEnd,
        )
      })

      dot.addEventListener('click', () => {
        if (this.currentStart === dotCellRef && this.currentEnd === dotCellRef)
          return

        this.node.dispatchEvent(
          new CustomEvent('select', {
            detail: { selected: dotCellRef },
          }),
        )
      })
    })
  }

  this.previous?.addEventListener('click', () => {
    if (!this.initHasHappened) return
    if (this.previous.disabled) return

    const end = this.stepSize > 1 ? this.currentEnd : this.currentStart
    const previousStep = Math.max(this.start, end - this.stepSize)

    this.viewport.removeAttribute('data-locked')
    this.node.dispatchEvent(
      new CustomEvent('select', { detail: { selected: previousStep } }),
    )
  })

  this.next?.addEventListener('click', () => {
    if (!this.initHasHappened || !this.next) return
    if (this.next.disabled) return

    const nextStep = Math.min(this.end, this.currentStart + this.stepSize)

    this.viewport.removeAttribute('data-locked')
    this.node.dispatchEvent(
      new CustomEvent('select', { detail: { selected: nextStep } }),
    )
  })

  this.node.addEventListener('select', ({ detail }) => {
    if (!this.initHasHappened) return

    const scrollOverride = detail?.override || false
    const behavior = detail?.behavior || scrollBehaviour
    const selectedIndex = detail?.selected || 0
    let selectedStep = selectedIndex

    if (this.stepSize > 1) {
      selectedStep = Math.floor(selectedIndex / this.stepSize) * this.stepSize
    }

    if (this.cells[selectedStep] === undefined) return
    if (
      this.cells[selectedStep].classList.contains('current-cell') &&
      this.cells[selectedStep + this.stepSize]?.classList.contains(
        'current-cell',
      ) &&
      this.stepSize > 1 &&
      !scrollOverride
    )
      return

    if (interactionDelay > 10) {
      this.cells.forEach((cell) => cell.classList.remove('current-cell'))
    }

    setTimeout(() => {
      this.viewport.scrollTo({
        top: 0,
        left:
          selectedStep === 0 ? 0 : (
            this.cells[selectedStep].offsetLeft - scrollOffsetLeft
          ),
        behavior,
      })
    }, interactionDelay)

    if (behavior === 'instant') {
      setTimeout(() => {
        scrollStateUpdate()
      }, interactionDelay + 50)
    }
  })

  const scrollStateUpdate = () => {
    this.currentStart = getCurrentStart()
    this.currentEnd = getCurrentEnd()

    this.previous?.toggleAttribute('disabled', this.currentStart === this.start)
    this.next?.toggleAttribute('disabled', this.currentEnd === this.end)

    this.node.dispatchEvent(new CustomEvent('settle', { detail: this }))
  }

  if ('onscrollend' in window) {
    this.viewport.addEventListener('scrollend', scrollStateUpdate)
  } else {
    this.viewport.addEventListener(
      'scroll',
      debounce(() => {
        clearTimeout(this.scrollEndTimer)
        this.scrollEndTimer = setTimeout(scrollStateUpdate, 90)
      }, 10),
    )
  }

  const startAutoPlay = () => {
    autoPlayTimer = setInterval(() => {
      let nextStep = this.currentStart + this.stepSize

      if (nextStep > this.end) {
        nextStep = this.loop ? this.start : this.end
      }

      this.node.dispatchEvent(
        new CustomEvent('select', { detail: { selected: nextStep } }),
      )
    }, autoPlay)
  }

  const stopAutoPlay = () => {
    clearInterval(autoPlayTimer)
  }

  const setup = () => {
    this.stepSize =
      stepSize ??
      Math.max(
        1,
        Array.from(node.querySelectorAll(`${cell}.current-cell`)).length,
      )

    scrollStateUpdate()

    if (this.loop) {
      startAutoPlay()
      this.node.addEventListener('mouseenter', stopAutoPlay)
      this.node.addEventListener('mouseleave', startAutoPlay)
    }

    if (this.dots.length > 0) {
      this.dots.forEach((dot) => {
        const dotCellRef = Number(dot.getAttribute('data-cell'))

        dot.classList.toggle(
          'active',
          dotCellRef >= this.currentStart && dotCellRef <= this.currentEnd,
        )
      })
    }

    if (!this.initHasHappened) {
      this.initHasHappened = true
      this.node.dispatchEvent(new CustomEvent('ready', { detail: this }))
    }
  }

  this.node.addEventListener('resize', () => {
    if (!this.initHasHappened) return
    if (
      this.initialHeight === this.viewport.getBoundingClientRect().height &&
      this.initialWidth === this.viewport.getBoundingClientRect().width
    )
      return

    this.stepSize =
      stepSize ??
      Math.max(
        1,
        Array.from(node.querySelectorAll(`${cell}.current-cell`)).length,
      )

    scrollOffsetLeft = parseFloat(getComputedStyle(this.viewport).paddingLeft)

    scrollStateUpdate()

    const resizeStart = this.cells.findIndex((ele) =>
      ele.classList.contains('current-cell'),
    )

    this.node.dispatchEvent(
      new CustomEvent('select', {
        detail: { selected: resizeStart, override: true },
      }),
    )
  })

  window.addEventListener(
    'resize',
    debounce(() => this.node.dispatchEvent(new CustomEvent('resize')), 100),
  )
}
